import { type Actr } from '@opcentral/typr-lib';
import { type User } from '@opcentral/typr-lib';

/**
 * SessionFileManager.ts - SessionFileManager
 * 
 * CHANGE HISTORY:
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
interface SessionFileManagerOptions {
  actr?: unknown; // Actr instance
  [key: string]: unknown;
}

/**
 * SessionFileManager class
 */
export class SessionFileManager {
  private actr: unknown;
  private options: any;
  
  constructor(options: SessionFileManagerOptions = {}) {
    this.options = {
      sessionsDir: options.sessionsDir || 'docs/sessions',
      historyDir: options.historyDir || 'docs/history',
      autoUpdate: options.autoUpdate !== false,
      ...options
    };
  }
  

  /**
   * Register SessionFile actions with Actr
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // createSessionFile action
    (this.actr as any).register('loggr:create-session-file', {
      handler: async (params: any) => {
        return await this.createSessionFile(params, null, null);
      },
      description: 'Create session file'
    });

    // updateSessionFile action
    (this.actr as any).register('loggr:update-session-file', {
      handler: async (params: any) => {
        return await this.updateSessionFile(params);
      },
      description: 'Update session file'
    });
  }

  /**
   * Generate session filename
   * Format: YYYY-MM-DD_sessionId_userId_machineId_description.md
   */
  generateSessionFilename(session: any, userInfo: any): string {
    const date = session.startTime.split('T')[0]; // YYYY-MM-DD
    const sessionId = session.id;
    const userId = userInfo?.shortId || userInfo?.id || 'unknown';
    const machineId = userInfo?.machineId ? 
      userInfo.machineId.replace(/[^a-z0-9]+/gi, '-').substring(0, 20) : 
      'unknown-machine';
    const description = session.description
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .substring(0, 30);
    
    return `${date}_${sessionId}_${userId}_${machineId}_${description}.md`;
  }
  
  /**
   * Generate session file content
   */
  generateSessionContent(session: any, userInfo: any, activities: any): string {
    const startTime = new Date(session.startTime);
    const endTime = session.endTime ? new Date(session.endTime) : null;
    const duration = endTime 
      ? Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
      : Math.round((Date.now() - startTime.getTime()) / 1000 / 60);
    
    let content = `---
session_id: ${session.id}
user_id: ${userInfo?.shortId || userInfo?.id || 'unknown'}
user_name: ${userInfo?.fullName || userInfo?.name || 'Unknown User'}
machine_id: ${userInfo?.machineId || 'unknown-machine'}
project: ${session.project || 'unknown-project'}
project_path: ${session.projectPath || 'unknown'}
start_time: ${session.startTime}
end_time: ${session.endTime || 'null'}
duration: ${duration} minutes
type: ${session.type}
status: ${(session as any).endTime ? 'completed' : 'active'}
---

# Development Session: ${(session as any).description}

**Session ID**: ${(session as any).id}  
**User**: ${(userInfo as any)?.shortId || (userInfo as any)?.id || 'unknown'} (${(userInfo as any)?.fullName || (userInfo as any)?.name || 'Unknown User'})  
**Machine**: ${(userInfo as any)?.machineId || 'unknown-machine'}  
**Project**: ${(session as any).project || 'unknown-project'}  
**Project Path**: \`${(session as any).projectPath || 'unknown'}\`  
**Date**: ${startTime.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}  
**Start**: ${startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} EDT  
**End**: ${endTime ? endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' EDT' : 'Active'}  
**Duration**: ${duration} minutes  
**Type**: ${(session as any).type}

---

## Session Overview

${(session as any).description}

**Project Context**: Working in \`${(session as any).project || 'unknown-project'}\` repository  
**Machine Context**: Running on \`${(userInfo as any)?.machineId || 'unknown-machine'}\`

---

## Activities

`;

    // Add activities
    if (activities && (activities as any).length > 0) {
      (activities as any).forEach((activity: any, index: number) => {
        const time = new Date((activity as any).timestamp);
        const timeStr = time.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          timeZone: 'America/New_York'
        });
        
        content += `### ${timeStr} - ${(activity as any).description}\n`;
        content += `- **Type**: ${(activity as any).type}\n`;
        
        if ((activity as any).context && Object.keys((activity as any).context).length > 0) {
          content += `- **Context**: ${JSON.stringify((activity as any).context, null, 2)}\n`;
        }
        
        content += `\n`;
      });
    } else {
      content += `No activities logged yet.\n\n`;
    }

    content += `---

## Statistics

- **Duration**: ${duration} minutes
- **Activities**: ${activities ? (activities as any).length : 0}
- **Status**: ${(session as any).endTime ? '✅ Completed' : '🟢 Active'}

---

## Session Metadata

\`\`\`json
{
  "session_id": "${(session as any).id}",
  "user_id": "${(userInfo as any)?.shortId || (userInfo as any)?.id || 'unknown'}",
  "user_name": "${(userInfo as any)?.fullName || (userInfo as any)?.name || 'Unknown User'}",
  "machine_id": "${(userInfo as any)?.machineId || 'unknown-machine'}",
  "project": "${(session as any).project || 'unknown-project'}",
  "project_path": "${(session as any).projectPath || 'unknown'}",
  "start_time": "${(session as any).startTime}",
  "end_time": ${(session as any).endTime ? `"${(session as any).endTime}"` : 'null'},
  "duration_minutes": ${duration},
  "type": "${(session as any).type}",
  "status": "${(session as any).endTime ? 'completed' : 'active'}",
  "activities": ${activities ? (activities as any).length : 0}
}
\`\`\`

---

**Session Status**: ${(session as any).endTime ? '✅ Completed' : '🟢 Active'}  
**Last Updated**: ${new Date().toISOString()}
`;

    return content;
  }
  
  /**
   * Create session file
   */
  async createSessionFile(session: unknown, userInfo: unknown, activities: unknown): Promise<any> {
    const filename = this.generateSessionFilename(session, userInfo);
    const filepath = `${this.options.sessionsDir}/${filename}`;
    const content = this.generateSessionContent(session, userInfo, activities);
    
    // In browser, can't write files directly
    if (typeof window !== 'undefined') {
      console.log('Session file content generated:', filepath);
      return { filepath, content };
    }
    
    // In Node.js, write to file
    if (typeof require !== 'undefined') {
      try {
        const fs = require('fs');
        const path = require('path');
        
        // Ensure directory exists
        const dir = path.dirname(filepath);
        if (!fs.existsSync(dir)) {
          fs.mkdirSync(dir, { recursive: true });
        }
        
        // Write file
        fs.writeFileSync(filepath, content, 'utf8');
        console.log(`✅ Session file created: ${filepath}`);
        
        return { filepath, content };
      } catch (error) {
        console.error('Failed to create session file:', error);
        return null;
      }
    }
    
    return { filepath, content };
  }
  
  /**
   * Update session file
   */
  async updateSessionFile(params: any): Promise<any> {
    return await this.createSessionFile(params, null, null);
  }
  
  /**
   * Get session file path
   */
  getSessionFilePath(session: unknown, userInfo: unknown): unknown {
    const filename = this.generateSessionFilename(session, userInfo);
    return `${this.options.sessionsDir}/${filename}`;
  }
}

export default SessionFileManager;
